import { useEffect, useRef, useState } from 'react';
import {
  IframeCallbacks,
  TokenizeFunction,
} from 'src/utilities/creditcard-iframe-types';
import { Environment } from 'src/utilities/environment';
import { pushError } from 'src/utilities/notifications';
import { getCreditCardEntryZoidComponent } from './getCreditCardEntryZoidComponent';

export type UseInitializeCreditCardIframeProps = {
  iframeContainerId: string;
  onIframeLoaded?: () => void;
  onValidCard?: () => void;
  onInvalidCard?: () => void;
};

type UseInitializeCreditCardIframeReturn = {
  isLoadingIframe: boolean;
  isLoadingComponent: boolean;
  cardIsValid: boolean;
  tokenize: TokenizeFunction;
};

// Mapping CMUI app environment values to what ccIframe expects
const envMapping = {
  [Environment.local]: 'preprod',
  [Environment.preprod]: 'preprod',
  [Environment.prod]: 'prod',
};

export const useInitializeCreditCardIframe = ({
  iframeContainerId,
  onIframeLoaded,
  onValidCard,
  onInvalidCard,
}: UseInitializeCreditCardIframeProps): UseInitializeCreditCardIframeReturn => {
  const [isLoadingIframe, setIsLoadingIframe] = useState<boolean>(true);
  const [isLoadingComponent, setIsLoadingComponent] = useState<boolean>(true);
  const [cardIsValid, setCardIsValid] = useState<boolean>(false);
  const iframeCallbacks = useRef<IframeCallbacks>(null);

  useEffect(() => {
    const abortController = new AbortController();

    async function initializeCreditCard(): Promise<void> {
      try {
        const creditCardEntryZoidComponent =
          await getCreditCardEntryZoidComponent({
            maxPollingRetries: 10,
            pollingIntervalMs: 500,
            abortSignal: abortController.signal,
          });

        // Only proceed if the component hasn't been unmounted
        if (!abortController.signal.aborted) {
          setIsLoadingComponent(false);

          creditCardEntryZoidComponent
            .init(
              {
                withCardholderName: true,
                env: envMapping[process.env.GH_ENV] || 'preprod',
                mobile: false,
                onValidCard: () => {
                  setCardIsValid(true);
                  onValidCard?.();
                },
                onTokenized: () => {
                  // We don't have to do anything here. The nonce is returned by the promise.
                  // Instead, we have to define this function, otherwise the iframe is not initialized.
                },
                onInvalidCard: () => {
                  setCardIsValid(false);
                  onInvalidCard?.();
                },
                onLoad: (callbacks: IframeCallbacks) => {
                  setIsLoadingIframe(false);
                  iframeCallbacks.current = callbacks;
                  onIframeLoaded?.();
                },
              },
              'iframe',
              `#${iframeContainerId}`
            )
            .render(`#${iframeContainerId}`);
        }
      } catch (error) {
        // Only update state if the component hasn't been unmounted and it's not an abort error
        if (
          abortController.signal.aborted ||
          (error instanceof Error && error.message.includes('aborted'))
        ) {
          return;
        }

        setIsLoadingIframe(false);
        setIsLoadingComponent(false);
        console.error('Error initializing Credit Card IFrame:', error);
        pushError('Unable to load the payment form. Please try again.');
      }
    }

    initializeCreditCard();

    return () => {
      abortController.abort();
    };
  }, [iframeContainerId, onIframeLoaded, onValidCard, onInvalidCard]);

  const tokenize: TokenizeFunction = (token, tokenizerUrl, options = {}) => {
    if (!iframeCallbacks.current) {
      throw new Error('Iframe callbacks not initialized');
    }
    return iframeCallbacks.current.tokenize(token, tokenizerUrl, options);
  };

  return {
    isLoadingIframe,
    isLoadingComponent,
    tokenize,
    cardIsValid,
  };
};
